import { newIdentifiant } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import { MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import type { DataReglementType, ReglementStateType } from '@/types';

/**
 * Machine à états de la saisie des règlements d'un encaissement.
 *
 * Ne connaît ni les factures ni les lignes au comptant : elle ne reçoit que le montant à
 * couvrir, ce qui la rend commune à l'encaissement de facture et à l'encaissement au comptant.
 * Les règles portées ici : modes utilisables sur la régie (PRE, CBI et PRI exclus), unicité des
 * modes à restriction unique, maximum de règlements, et totaux dérivés de la saisie.
 *
 * @param montantAEncaisser Montant que les règlements doivent couvrir — reste des factures
 *   sélectionnées pour un encaissement de facture, total du tableau au comptant.
 */
const useReglementsEncaissement = (montantAEncaisser: number) => {
  // État contenant les informations sur les règlements.
  const [reglements, setReglements] = React.useState<ReglementStateType[]>();

  const { regieModesReglement } = React.useContext(ModeReglementContext);

  // Calculer les codes des modes déjà utilisés (parmi ceux à restriction unique)
  const modesUtilises = React.useMemo(() => {
    const codesUtilises: string[] = [];
    reglements?.forEach((reglement) => {
      const modeReglement = _.find(regieModesReglement, {
        identifiant: reglement.identifiantModeReglement,
      });
      if (modeReglement && MODES_REGLEMENTS.RESTRICTION_UNIQUE.includes(modeReglement.code)) {
        codesUtilises.push(modeReglement.code);
      }
    });
    return codesUtilises;
  }, [reglements, regieModesReglement]);

  // On retire PRE, CBI et PRI quoi qu'il arrive pendant l'encaissement
  const filteredModesReglements = React.useMemo(
    () =>
      _.filter(
        regieModesReglement,
        ({ code }) =>
          code !== MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET &&
          code !== MODES_REGLEMENTS.PRELEVEMENT_INTERNET &&
          code !== MODES_REGLEMENTS.PRELEVEMENT,
      ),
    [regieModesReglement],
  );

  // Identifiant du mode de règlement numéraire s'il est actif sur la régie
  const identifiantModeReglementNumeraire = React.useMemo(
    () => _.find(filteredModesReglements, { code: MODES_REGLEMENTS.NUMERAIRE })?.identifiant,
    [filteredModesReglements],
  );

  /**
   * Reste à couvrir et total réglé en numéraire (contrôle du plafond réglementaire).
   *
   * L'accumulation part du montant à encaisser et soustrait chaque règlement, comme le faisait
   * la modale d'encaissement de facture : un règlement fraîchement ajouté n'a pas encore de
   * montant, les deux valeurs sont alors `NaN` — ce qui est sans effet puisque toute comparaison
   * avec `NaN` est fausse et que la validation est de toute façon bloquée tant que la saisie
   * n'est pas complète.
   */
  const totaux = React.useMemo(() => {
    let resteReglements = montantAEncaisser;
    let numeraire = 0;

    _.forEach(reglements, ({ montant, identifiantModeReglement }) => {
      if (identifiantModeReglementNumeraire === identifiantModeReglement) {
        numeraire += montant;
      }

      resteReglements -= montant;
    });

    return {
      resteReglements: parseFloat(resteReglements.toFixed(2)),
      montantNumeraire: parseFloat(numeraire.toFixed(2)),
    };
  }, [identifiantModeReglementNumeraire, montantAEncaisser, reglements]);

  // Vrai si tous les reglements sont complets, sinon faux.
  const reglementsComplet = React.useMemo(
    () => _.every(reglements, { complet: true }),
    [reglements],
  );

  // Vrai s'il reste au moins un mode de règlement sélectionnable.
  const resteModeReglementDisponible = React.useMemo(
    () =>
      !_.isEmpty(_.filter(filteredModesReglements, ({ code }) => !modesUtilises.includes(code))),
    [filteredModesReglements, modesUtilises],
  );

  /**
   * Réinitialise tous les règlements saisis.
   */
  const handleReglementResetAll = React.useCallback(() => {
    setReglements([
      {
        identifiantModeReglement: _.head(filteredModesReglements)?.identifiant,
        identifiant: newIdentifiant(),
        complet: false,
      },
    ]);
  }, [filteredModesReglements]);

  /**
   * Met à jour les informations d'un règlement spécifique dans la liste des règlements.
   *
   * @param {number} indice - L'indice du règlement à mettre à jour.
   * @param {object} data - Les nouvelles données à assigner au règlement.
   * @param {boolean} complet - Indique si le règlement est complet ou non.
   */
  const handleReglementChange = React.useCallback(
    (indice: number) => (data: DataReglementType, complet: boolean) => {
      setReglements((prev) =>
        _.map(prev, (reglement, index) =>
          indice === index
            ? {
                identifiantModeReglement: reglement.identifiantModeReglement,
                identifiant: reglement.identifiant,
                ...data,
                complet,
              }
            : reglement,
        ),
      );
    },
    [],
  );

  /**
   * Réinitialise les informations d'un règlement spécifique dans la liste des règlements.
   *
   * @param {number} indice - L'indice du règlement à réinitialiser.
   * @param {number} identifiantModeReglement - L'identifiant du futur mode de règlement.
   */
  const handleReglementReset = React.useCallback(
    (indice: number) => (identifiantModeReglement: number) => {
      setReglements((prev) =>
        _.map(prev, (reglement, index) =>
          indice === index
            ? {
                // On garde quand même l'identifiant pour pas remount la ligne complètement
                identifiant: reglement.identifiant,
                identifiantModeReglement,
                complet: false,
              }
            : reglement,
        ),
      );
    },
    [],
  );

  /**
   * Supprime un règlement de la liste des règlements.
   *
   * @param {number} indice - L'indice du règlement à supprimer.
   */
  const handleReglementMultipleDelete = React.useCallback((indice: number) => {
    setReglements((prev) => _.filter(prev, (d, index) => index !== indice));
  }, []);

  /**
   * Ajoute un règlement supplémentaire à la liste des règlements.
   */
  const handleReglementMultipleAdd = React.useCallback(() => {
    // On sélectionne le premier mode de règlement disponible
    // (qui n'est pas déjà utilisé parmi les modes à restriction unique)
    const nextModeReglement = _.head(
      _.filter(filteredModesReglements, ({ code }) => !modesUtilises.includes(code)),
    );

    setReglements([
      {
        identifiantModeReglement: nextModeReglement?.identifiant,
        identifiant: newIdentifiant(),
      },
      ...reglements,
    ]);
  }, [filteredModesReglements, modesUtilises, reglements]);

  /**
   * Retourne les codes des modes de règlement à désactiver pour un règlement donné.
   * Désactive les modes à restriction unique déjà utilisés, sauf si c'est le mode actuel.
   *
   * @param {ReglementStateType} rglmt - Le règlement à vérifier.
   * @returns {string[]} - Les codes des modes de règlement à désactiver.
   */
  const getModesReglementsDesactives = React.useCallback(
    (rglmt: ReglementStateType) => {
      const modeReglementActuel = _.find(regieModesReglement, {
        identifiant: rglmt.identifiantModeReglement,
      });
      // Désactiver les modes à restriction unique déjà utilisés, sauf le mode actuel
      return modesUtilises.filter((code) => code !== modeReglementActuel?.code);
    },
    [modesUtilises, regieModesReglement],
  );

  // Initialise les règlements
  React.useEffect(() => {
    handleReglementResetAll();
  }, [handleReglementResetAll]);

  return {
    reglements,
    filteredModesReglements,
    modesUtilises,
    identifiantModeReglementNumeraire,
    reglementsComplet,
    resteModeReglementDisponible,
    montantNumeraire: totaux.montantNumeraire,
    resteReglements: totaux.resteReglements,
    handleReglementResetAll,
    handleReglementChange,
    handleReglementReset,
    handleReglementMultipleDelete,
    handleReglementMultipleAdd,
    getModesReglementsDesactives,
  };
};

export default useReglementsEncaissement;
